/* Enhance real links and Shopify's native contact form; no app or network dependency. */
(() => {
  'use strict';

  const formId = 'NvFloatingContactForm';

  function initContactDock() {
    const dock = document.querySelector('[data-nv-contact-dock]');
    if (!dock || dock.dataset.nvContactReady === 'true') return;
    dock.dataset.nvContactReady = 'true';
    const dialog = document.querySelector('[data-nv-contact-dialog]');
    const spacer = document.querySelector('.nv-contact-dock-spacer');
    let layoutFrame = 0;
    let observedPreviewBar = null;
    let previewResizeObserver = null;
    let previewAttributesObserver = null;
    let previewInsertionObserver = null;

    function updateViewport() {
      const viewport = window.visualViewport;
      const viewportTop = (viewport && viewport.offsetTop) || 0;
      const viewportHeight = viewport ? viewport.height : window.innerHeight;
      const viewportBottom = viewportTop + viewportHeight;
      const previewBar = document.getElementById('PBarNextFrameWrapper');
      let previewHeight = 0;
      if (previewBar) {
        const rect = previewBar.getBoundingClientRect();
        const style = window.getComputedStyle(previewBar);
        if (style.position === 'fixed' && style.display !== 'none' && style.visibility !== 'hidden' &&
          rect.width >= window.innerWidth * .5 && rect.height > 0 && rect.top < viewportBottom && rect.bottom >= viewportBottom - 12) {
          previewHeight = Math.max(0, viewportBottom - Math.max(viewportTop, rect.top));
        }
        if (previewBar !== observedPreviewBar) {
          observedPreviewBar = previewBar;
          if (previewResizeObserver) previewResizeObserver.disconnect();
          if (previewAttributesObserver) previewAttributesObserver.disconnect();
          if (typeof window.ResizeObserver === 'function') {
            previewResizeObserver = new window.ResizeObserver(scheduleViewport);
            previewResizeObserver.observe(previewBar);
          }
          if (typeof window.MutationObserver === 'function') {
            previewAttributesObserver = new window.MutationObserver(scheduleViewport);
            previewAttributesObserver.observe(previewBar, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
          }
          if (previewInsertionObserver) previewInsertionObserver.disconnect();
        }
      }
      dock.style.setProperty('--nv-contact-preview-height', previewHeight + 'px');
      if (spacer) spacer.style.setProperty('--nv-contact-preview-height', previewHeight + 'px');
      if (dialog) {
        dialog.style.setProperty('--nv-contact-visible-height', viewportHeight + 'px');
        dialog.style.setProperty('--nv-contact-visible-top', viewportTop + 'px');
      }
      const typing = document.activeElement && document.activeElement.matches('input, textarea, select, [contenteditable="true"]');
      dock.dataset.nvKeyboard = viewport && window.innerHeight - viewport.height > 150 && typing ? 'true' : 'false';
    }

    function scheduleViewport() {
      if (layoutFrame) return;
      layoutFrame = window.requestAnimationFrame(() => {
        layoutFrame = 0;
        updateViewport();
      });
    }

    updateViewport();
    window.addEventListener('resize', scheduleViewport, { passive: true });
    window.addEventListener('orientationchange', scheduleViewport, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleViewport, { passive: true });
      window.visualViewport.addEventListener('scroll', scheduleViewport, { passive: true });
    }
    document.addEventListener('focusin', scheduleViewport);
    document.addEventListener('focusout', scheduleViewport);
    if (!observedPreviewBar && typeof window.MutationObserver === 'function') {
      // Shopify inserts this wrapper after the theme; watch for its arrival only.
      previewInsertionObserver = new window.MutationObserver(scheduleViewport);
      previewInsertionObserver.observe(document.body, { childList: true, subtree: true });
    }

    const topLink = dock.querySelector('[data-nv-contact-top]');
    if (topLink) {
      topLink.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const target = document.getElementById('NvPageTop');
        if (target) {
          const originalTabindex = target.getAttribute('tabindex');
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
          if (originalTabindex === null) target.removeAttribute('tabindex');
          else target.setAttribute('tabindex', originalTabindex);
        }
        window.scrollTo({ top: 0, left: 0, behavior: reduceMotion ? 'instant' : 'smooth' });
      });
    }

    const opener = dock.querySelector('[data-nv-contact-open]');
    if (!dialog || !opener || typeof dialog.showModal !== 'function') return;
    opener.setAttribute('aria-haspopup', 'dialog');
    opener.setAttribute('aria-controls', dialog.id);
    opener.setAttribute('aria-expanded', 'false');

    const form = dialog.querySelector('#' + formId);
    const currentUrl = new URL(window.location.href);
    const belongsToFloatingForm = currentUrl.hash === '#' + formId ||
      (currentUrl.searchParams.get('nv_contact') === '1' && !currentUrl.hash);
    const status = dialog.querySelector('[data-nv-contact-status]');
    let returnFocus = null;
    let pointerStartedOnBackdrop = false;

    function clearServerInvalidState() {
      if (!form) return;
      form.querySelectorAll('[aria-describedby]').forEach((field) => {
        const ids = field.getAttribute('aria-describedby').split(/\s+/);
        if (!ids.includes('NvFloatingContactErrors')) return;
        field.removeAttribute('aria-invalid');
        const remainingIds = ids.filter((id) => id !== 'NvFloatingContactErrors');
        if (remainingIds.length) field.setAttribute('aria-describedby', remainingIds.join(' '));
        else field.removeAttribute('aria-describedby');
      });
    }

    // Shopify exposes contact feedback to all contact forms rendered on a page.
    // An inline form's response must never be presented as this form's response.
    if (status && !belongsToFloatingForm) {
      status.hidden = true;
      clearServerInvalidState();
    }

    function updateFormContext() {
      if (!form) return;
      const sourceInput = form.querySelector('[data-nv-contact-source]');
      const currentUrl = new URL(window.location.href);
      if (sourceInput) {
        const canonicalLink = document.querySelector('link[rel="canonical"]');
        let sourceUrl;
        try {
          sourceUrl = new URL(canonicalLink ? canonicalLink.href : currentUrl.origin + currentUrl.pathname);
        } catch (_) {
          sourceUrl = new URL(currentUrl.origin + currentUrl.pathname);
        }
        sourceUrl.search = '';
        sourceUrl.hash = '';
        // Keep product identity, without sending preview tokens or campaign data.
        ['view', 'variant'].forEach((key) => {
          if (currentUrl.searchParams.has(key)) sourceUrl.searchParams.set(key, currentUrl.searchParams.get(key));
        });
        sourceInput.value = document.title + ' — ' + sourceUrl.href;
      }
      const returnInput = form.querySelector('input[name="return_to"]');
      if (returnInput) {
        currentUrl.searchParams.delete('contact_posted');
        currentUrl.searchParams.set('nv_contact', '1');
        returnInput.value = currentUrl.pathname + currentUrl.search + '#' + formId;
      }
    }

    function openDialog(focusTarget) {
      if (dialog.open) return;
      updateFormContext();
      returnFocus = document.activeElement;
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
      document.body.classList.add('nv-contact-dialog-open');
      // Focus text first to avoid opening a mobile keyboard before the visitor is ready.
      const target = focusTarget || dialog.querySelector('.nv-contact-dialog__title') || dialog.querySelector('[data-nv-contact-close]');
      if (target) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
      dialog.scrollTop = 0;
    }

    opener.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openDialog();
    });

    dialog.querySelectorAll('[data-nv-contact-close]').forEach((button) => {
      button.addEventListener('click', () => dialog.close());
    });

    function isBackdrop(event) {
      const rect = dialog.getBoundingClientRect();
      return event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
    }

    dialog.addEventListener('pointerdown', (event) => {
      pointerStartedOnBackdrop = isBackdrop(event);
    });
    dialog.addEventListener('click', (event) => {
      if (pointerStartedOnBackdrop && isBackdrop(event)) dialog.close();
      pointerStartedOnBackdrop = false;
    });
    // The browser supplies Escape handling, background inertness and the focus trap.
    dialog.addEventListener('close', () => {
      document.body.classList.remove('nv-contact-dialog-open');
      opener.setAttribute('aria-expanded', 'false');
      if (status && status.dataset.state === 'success') status.hidden = true;
      if (belongsToFloatingForm) {
        // Remove only this form's transient return state; preserve product views.
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('nv_contact');
        if (status) cleanUrl.searchParams.delete('contact_posted');
        if (cleanUrl.hash === '#' + formId) cleanUrl.hash = '';
        try {
          window.history.replaceState(window.history.state, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
        } catch (_) {
          // A sandboxed preview can disallow history updates. Form use still works.
        }
      }
      const target = returnFocus && returnFocus.isConnected && returnFocus !== document.body ? returnFocus : opener;
      target.focus({ preventScroll: true });
    });

    if (form) {
      updateFormContext();
      form.addEventListener('submit', updateFormContext);
      form.addEventListener('invalid', (event) => event.target.setAttribute('aria-invalid', 'true'), true);
      form.addEventListener('input', (event) => {
        if (event.target.validity && event.target.validity.valid) event.target.removeAttribute('aria-invalid');
      });
    }

    if (belongsToFloatingForm && status && ['success', 'error'].includes(status.dataset.state)) {
      openDialog(status);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initContactDock, { once: true });
  else initContactDock();
  document.addEventListener('shopify:section:load', initContactDock);
})();
